import React from "react";
import { ModelViewerEmbed } from "../3d/model.viewer.embed";
import { isImageLikeUrl } from "../artworks/importedMetadata";
import { getProxiedImageUrl } from "../../utils/imageProxy";
import { resolveRuntimeMediaUrl } from "../../utils/runtimeMedia";
import { useRuntimeMediaDelivery } from "../../hooks/useRuntimeMediaDelivery";
import type { RuntimeMediaManifest } from "../../utils/runtimeConfig";
import workspaceTheme from "./workspaceTheme.module.css";

const BASE_API = process.env.REACT_APP_BASE_API || "";

type Props = {
  type: string | null | undefined;
  title?: string | null;
  previewUrl?: string | null;
  dataUrl?: string | null;
  originalUrl?: string | null;
  runtimeMedia?: RuntimeMediaManifest | null;
  preferStaticPoster?: boolean;
  variant?: "card" | "detail" | "list" | "sheet";
};

const withProxy = (
  value?: string | null,
  trustedOrigin?: string,
) => (value ? getProxiedImageUrl(value, BASE_API, trustedOrigin ? [trustedOrigin] : []) : null);

const resolveMediaUrl = (
  value?: string | null,
  trustedOrigin?: string,
) => {
  if (!value) {
    return null;
  }

  return /^https?:\/\//i.test(value) ? withProxy(value, trustedOrigin) : value;
};

export const WorkspaceMediaPreview = ({
  type,
  title,
  previewUrl,
  dataUrl,
  originalUrl,
  runtimeMedia,
  preferStaticPoster = false,
  variant = "card",
}: Props) => {
  const mediaDelivery = useRuntimeMediaDelivery();
  const normalizedType = (type || "").trim().toLowerCase();
  const cdnDomain = mediaDelivery?.cdnDomain;
  const runtimeImageUrl = resolveRuntimeMediaUrl(
    previewUrl || dataUrl || originalUrl,
    "image",
    mediaDelivery,
    runtimeMedia,
  );
  const imageUrl = resolveMediaUrl(runtimeImageUrl, cdnDomain);
  const mediaUrl = normalizedType === "video"
    ? resolveRuntimeMediaUrl(dataUrl || originalUrl || previewUrl, "video", mediaDelivery, runtimeMedia)
    : normalizedType === "sound"
      ? resolveRuntimeMediaUrl(dataUrl || originalUrl || previewUrl, "audio", mediaDelivery, runtimeMedia)
      : dataUrl || originalUrl || previewUrl || null;
  const modelUrl = resolveRuntimeMediaUrl(originalUrl || dataUrl, "model3d", mediaDelivery, runtimeMedia);
  const modelUsesImagePlaceholder = normalizedType === "3dmodel" && isImageLikeUrl(modelUrl);
  const staticPosterUrl = resolveMediaUrl(
    resolveRuntimeMediaUrl(previewUrl, "image", mediaDelivery),
    cdnDomain,
  ) || "/assets/3d-model.png";
  const modelPosterUrl = modelUsesImagePlaceholder ? imageUrl : staticPosterUrl;
  const isListVariant = variant === "list";
  const frameClassName = [
    workspaceTheme.previewFrame,
    variant === "card" ? workspaceTheme.previewFrameCard : "",
    variant === "detail" ? workspaceTheme.previewFrameDetail : "",
    variant === "sheet" ? workspaceTheme.previewFrameSheet : "",
    variant === "list" ? workspaceTheme.previewFrameList : "",
  ].filter(Boolean).join(" ");
  const imageClassName = [
    workspaceTheme.previewMedia,
    isListVariant ? workspaceTheme.previewMediaList : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={frameClassName}>
      {normalizedType === "3dmodel" && (preferStaticPoster || isListVariant || modelUsesImagePlaceholder) ? (
        <img className={imageClassName} src={modelPosterUrl || staticPosterUrl} alt={title || "3D artwork preview"} />
      ) : null}
      {normalizedType === "3dmodel" && !preferStaticPoster && !isListVariant && !modelUsesImagePlaceholder && modelUrl ? (
        <ModelViewerEmbed modelUrl={modelUrl} className="w-100 h-100" />
      ) : null}
      {normalizedType === "video" && mediaUrl && !isListVariant ? (
        <video controls className={workspaceTheme.previewMedia} src={resolveMediaUrl(mediaUrl, cdnDomain) || mediaUrl} />
      ) : null}
      {normalizedType === "video" && imageUrl && isListVariant ? (
        <img className={imageClassName} src={imageUrl} alt={title || "Video artwork preview"} />
      ) : null}
      {normalizedType === "video" && !imageUrl && isListVariant ? (
        <div className={workspaceTheme.previewTypeTile}>Video</div>
      ) : null}
      {normalizedType === "sound" && mediaUrl && !isListVariant ? (
        <audio controls className={workspaceTheme.previewAudio} src={mediaUrl} />
      ) : null}
      {normalizedType === "sound" && isListVariant ? (
        <div className={workspaceTheme.previewTypeTile}>Audio</div>
      ) : null}
      {normalizedType === "image" && imageUrl ? (
        <img className={imageClassName} src={imageUrl} alt={title || "Artwork preview"} />
      ) : null}
      {!["3dmodel", "video", "sound", "image"].includes(normalizedType) && imageUrl ? (
        <img className={imageClassName} src={imageUrl} alt={title || "Artwork preview"} />
      ) : null}
      {!["3dmodel", "video", "sound", "image"].includes(normalizedType) && !imageUrl && isListVariant ? (
        <div className={workspaceTheme.previewTypeTile}>Artwork</div>
      ) : null}
      {!mediaUrl && !imageUrl ? (
        <div className={workspaceTheme.previewFallback}>Preview unavailable for this artwork.</div>
      ) : null}
    </div>
  );
};
